import { Button, Typography } from '@navi/web-ui/lib/primitives';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CallHistory from 'src/components/CallHistory/CallHistory';
import Dropdown from 'src/components/Dropdown';
import { SelectPickerOptionProps } from 'src/components/interfaces';
import { setAuthData } from 'src/reducers/commonSlice';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import {
  ECallingType,
  FeedbackFormAction,
  FeedbackFormKind,
  IFeedbackForm,
  InteractionType
} from '../../interfaces/index.type';
import CallBridge from '../CallBridge/CallBridge';
import styles from './index.module.scss';
import { readQueryParams } from '@cp/utils/QueryParamsHelper';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { CaseStatus, IPhoneNumberLimit, LIMIT_TYPE } from '../../../interfaces/CaseDetail.type';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/TooltipV2/TooltipV2';
import cx from 'classnames';
import { setIsFeedbackFormDirty } from '../../reducer/feedbackSlice';
import PocNumbersTag from '../../../components/PocTag/Index';
import { POC_TAG_SOURCES } from '@cp/src/pages/CaseDetails/components/PocTag/constants';
import GoogleSearchIcon from '@cp/assets/images/icons/GoogleSearchIcon';
import { toast } from '@primitives/Toast';
import { searchGoogle } from '@cp/utils/commonUtils';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import ClockIcon from '@cp/assets/icons/ClockIcon';
import dayjs from 'dayjs';
import { CALL_SOURCE_LIMIT } from '@cp/pages/CaseDetails/constants/Overview.constant';
import { LIMIT_CALLS, PAUSE_CASE_LH_VISIBILITY_ROLES } from '@cp/src/constants/Common.constants';

interface DefaultContactsProps {
  feedback: IFeedbackForm;
  dispatchFeedback: React.Dispatch<FeedbackFormAction>;
  currentCallType: ECallingType;
}

interface Telephone {
  value: string;
  label: string;
  source: JSX.Element;
  rightAdornment: JSX.Element;
  dailyLimit: IPhoneNumberLimit;
  hourlyLimit: IPhoneNumberLimit;
  identifier?: string;
}

const DefaultContacts: React.FC<DefaultContactsProps> = props => {
  const { feedback, dispatchFeedback, currentCallType } = props;
  const dispatch = useDispatch();
  const { loanId = '', customerId = '' } = useParams();
  const { AMEYO: ameyoQueryParams } = readQueryParams();

  const telephones = useSelector(
    (state: RootState) => state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.telephonesv2
  );
  const user = useSelector((state: RootState) => state?.common?.userData);

  const editAccessFlag = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag
  );
  const addedPhoneNumber = useSelector((state: RootState) => state.feedBack.addedPhoneNumber);
  const addedIdentifier = useSelector((state: RootState) => state.feedBack.addedIdentifier);
  const [isOptionDisabled, setIsOptionDisabled] = React.useState(false);
  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const isCasePaused = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.pause?.data?.isPaused
  );
  const hasVisibilityRole = user?.roles?.some(role =>
    PAUSE_CASE_LH_VISIBILITY_ROLES.includes(role)
  );

  const disableCTAs = isGlobalAccessGiven ? !editAccessFlag : false;

  const toSlashUserPage = () => {
    const slashURL = `https://navi.slashrtc.in/index.php/ssoLogin?crmUniqueId=${user.encryptedCrmId}&usernameId=${user.slashUsername}&requestOrigin=${window.location.origin}`;
    window.open(slashURL, '_blank', 'noopener,noreferrer');
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_OpenSlashCTA);
    dispatch(setAuthData({ slashTabOpened: true }));
  };

  const displaySourceText = (sources: string[]) => {
    if (sources?.length === 0) {
      return <span className={styles.sourceTextNames}>{'No sources'}</span>;
    }
    if (sources?.length === 1) {
      return <span className={styles.sourceTextNames}>{'(' + sources[0] + ')'}</span>;
    }
    if (sources?.length === 2) {
      return (
        <span className={styles.sourceTextNames}>{'(' + sources[0] + ', ' + sources[1] + ')'}</span>
      );
    }
    if (sources?.length > 2) {
      return <span className={styles.sourceTextNames}>{'(' + sources[0] + ', ' + sources[1]}</span>;
    }
  };

  const telephoneList = useMemo(() => {
    const newTelephoneList =
      telephones?.data?.map(tel => ({
        value: tel.number,
        label: tel.number,
        isPocNumber: tel.isPoc,
        source: (
          <Typography className={styles.sourceText} variant="p3">
            {displaySourceText(tel?.sources)}
            {tel.sources.length > CALL_SOURCE_LIMIT && (
              <>
                <Tooltip>
                  <TooltipTrigger>
                    <div>
                      <span className={styles.sourceTextExtras}>
                        {' +' + (tel.sources.length - CALL_SOURCE_LIMIT) + ' more'}
                      </span>
                      {')'}
                    </div>
                  </TooltipTrigger>

                  <TooltipContent className={styles.tooltip}>
                    {[...tel.sources.slice(CALL_SOURCE_LIMIT, tel.sources.length)].map(
                      (i, index) => (
                        <>
                          <span>
                            {i} {index < tel.sources.length - CALL_SOURCE_LIMIT - 1 ? ', ' : ''}
                          </span>
                        </>
                      )
                    )}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </Typography>
        ),
        callHistory: tel?.callHistory,
        rightAdornment: null,
        dailyLimit: tel?.limit[LIMIT_TYPE.DAILY],
        hourlyLimit: tel?.limit[LIMIT_TYPE.HOURLY],
        identifier: tel?.numberCustomId
      })) || [];

    if (feedback.telephoneNotRequired) {
      newTelephoneList.unshift({
        value: '',
        label: 'Phone number not required',
        source: (
          <Typography className={styles.sourceText} variant="p3">
            {'No sources'}
          </Typography>
        ),
        rightAdornment: null
      });
    }

    return newTelephoneList;
  }, [telephones?.data, feedback.telephoneNotRequired]);

  // const previousSelectedTelephone = useRef<Telephone | undefined>(telephoneList?.[0]);
  const firstAvailableNumber = useMemo(
    () =>
      telephoneList?.find(
        t => t?.dailyLimit?.callRemaining !== 0 && t?.hourlyLimit?.callRemaining !== 0
      ) || telephoneList?.[0],
    [telephoneList]
  );
  const previousSelectedTelephone = useRef<Telephone | undefined>(firstAvailableNumber);
  const getDefaultPhoneNumber = useMemo(() => {
    if (ameyoQueryParams?.phoneNumber) {
      return telephoneList?.find(t => t.value === ameyoQueryParams?.phoneNumber);
    }
    const firstAvailableNumber = telephoneList?.find(
      t => t?.dailyLimit?.callRemaining !== 0 && t?.hourlyLimit?.callRemaining !== 0
    );
    const isNumberInvalid = !telephoneList?.find(
      t => t.identifier === previousSelectedTelephone.current?.identifier
    );
    const currentSelectedTelephone = isNumberInvalid
      ? firstAvailableNumber
      : previousSelectedTelephone.current
      ? previousSelectedTelephone.current
      : firstAvailableNumber;
    !feedback.telephoneNotRequired &&
      dispatchFeedback({
        type: FeedbackFormKind.UPDATE_TELEPHONES,
        payload: { telephone: currentSelectedTelephone?.value as string }
      });
    if (
      currentSelectedTelephone?.dailyLimit?.callRemaining === 0 ||
      currentSelectedTelephone?.hourlyLimit?.callRemaining === 0
    ) {
      setIsOptionDisabled(true);
    } else {
      setIsOptionDisabled(false);
    }
    return currentSelectedTelephone ? currentSelectedTelephone : telephoneList?.[0];
  }, [telephoneList, ameyoQueryParams?.phoneNumber, feedback.telephone]);

  useEffect(() => {
    if (
      telephoneList?.find(t => t.value === addedPhoneNumber && t.identifier === addedIdentifier) &&
      addedIdentifier
    ) {
      previousSelectedTelephone.current = telephoneList?.find(t => t.value === addedPhoneNumber);

      dispatchFeedback({
        type: FeedbackFormKind.UPDATE_TELEPHONES,
        payload: { telephone: addedPhoneNumber, isDefaultCallRequired: true }
      });
    }
  }, [addedIdentifier, telephoneList]);

  const handleTelephoneChange = useCallback(
    (option: SelectPickerOptionProps) => {
      if (option.value !== '') {
        previousSelectedTelephone.current = telephoneList?.find(t => t.value === option.value);
      }
      dispatchFeedback({
        type: FeedbackFormKind.UPDATE_TELEPHONES,
        payload: { telephone: option.value as string }
      });
      if (
        previousSelectedTelephone.current?.dailyLimit?.callRemaining === 0 ||
        previousSelectedTelephone.current?.hourlyLimit?.callRemaining === 0
      ) {
        setIsOptionDisabled(true);
      } else {
        setIsOptionDisabled(false);
      }
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_PhoneNumberDropdown, {
        phone: option?.value,
        source: option?.source
      });
      dispatch(setIsFeedbackFormDirty(true));
    },
    [telephoneList]
  );

  const getSelectedSourceLabels = useCallback(() => {
    if (feedback.telephoneNotRequired) {
      return '';
    }
    const firstAvailableNumber =
      telephoneList?.find(
        t => t?.dailyLimit?.callRemaining !== 0 && t?.hourlyLimit?.callRemaining !== 0
      ) || telephoneList?.[0];
    const selectedSourceLabels =
      telephones?.data?.find(t => t.number === previousSelectedTelephone.current?.value)?.sources ||
      telephones?.data?.find(t => t.number === firstAvailableNumber?.value)?.sources;
    return selectedSourceLabels?.join(', ');
  }, [previousSelectedTelephone.current, feedback.telephone]);

  const handleGoogleSearch = () => {
    if (feedback.telephoneNotRequired) {
      return;
    }
    if (!feedback.telephone) {
      toast('No telephone selected', { type: 'error' });
      return;
    }
    searchGoogle(feedback.telephone);
  };

  const disableGoogleSearchButton = !feedback.telephone;

  const disableCTAsForSlash = disableCTAs || (isCasePaused && !hasVisibilityRole);

  return (
    <div
      key={`${feedback.telephone}_${feedback.initialOptionId}_${feedback.initialQuestionId}_${feedback.selectedChip}`}
      className={styles.defaultContacts}
    >
      <div className={styles.phoneNumberSelectContainer}>
        <div className={styles.lables}>
          <Typography as={'p'} className={styles.lightText} variant={'p4'}>
            Select number
          </Typography>
          <Button onClick={toSlashUserPage} disabled={disableCTAsForSlash} variant={'text'}>
            Open Slash
          </Button>
        </div>
        <div className={styles.dropdownContainer}>
          <Dropdown
            options={telephoneList}
            onSelectionChange={handleTelephoneChange}
            containerClasses={styles.telephoneDropdown}
            disabled={disableCTAs}
            innerContainerStyle={cx(
              {
                [styles.disabledDropdown]: feedback.telephoneNotRequired && !feedback.telephone
              },
              styles.teleDropdownBorder,
              'rr-block'
            )}
            placeholder={
              feedback.telephoneNotRequired ? 'Phone number not required' : 'Choose a contact'
            }
            defaultSelected={
              feedback.telephoneNotRequired && !feedback.telephone ? '' : getDefaultPhoneNumber
            }
            multiSelect={false}
            showSourceAtBottom={true}
            selectedLabels={getSelectedSourceLabels()}
            customOptionTemplate={(option, metadata) => {
              return (
                <>
                  {metadata?.isPocNumber ? (
                    <PocNumbersTag
                      callHistory={metadata?.callHistory}
                      source={POC_TAG_SOURCES.FEEDBACK_FORM}
                      option={option}
                      dailyLimit={metadata?.dailyLimit}
                      hourlyLimit={metadata?.hourlyLimit}
                    />
                  ) : (
                    <div className="w-[100%]">
                      <div className="flex items-center justify-between">
                        <Typography variant="p3" className="text-sm rr-block">
                          {option}
                        </Typography>
                        <CallHistory list={metadata?.callHistory} noOfRecords={5} />
                      </div>
                      {/* {metadata?.dailyLimit?.callRemaining === 0 ? (
                        <div className="flex items-center gap-[4px] mt-[8px]">
                          <ClockIcon />
                          <Typography variant="h5" color="var(--grayscale-2)">
                            {LIMIT_CALLS.NEXT_CALL_ATTEMPT}
                            <Typography variant="h5" as="span" color="var(--orange-darkbase)">
                              {LIMIT_CALLS.TOMORROW}
                            </Typography>
                          </Typography>
                        </div>
                      ) : metadata?.hourlyLimit?.nextCallingTime ? (
                        <div className="flex items-center gap-[4px] mt-[8px]">
                          <ClockIcon />
                          <Typography variant="h5" color="var(--grayscale-2)">
                            {LIMIT_CALLS.NEXT_CALL_ATTEMPT}
                            {dayjs(metadata?.hourlyLimit?.nextCallingTime).isAfter(
                              dayjs(),
                              'day'
                            ) ? (
                              <Typography variant="h5" as="span" color="var(--orange-darkbase)">
                                {LIMIT_CALLS.TOMORROW}
                              </Typography>
                            ) : (
                              <Typography variant="h5" as="span" color="var(--orange-darkbase)">
                                {'from '}
                                {dayjs(metadata?.hourlyLimit?.nextCallingTime).format('h:mm A')}
                              </Typography>
                            )}
                          </Typography>
                        </div>
                      ) : null} */}
                    </div>
                  )}
                </>
              );
            }}
            selectPickerItemClass={styles.selectedPickerItemLimits}
          />
          {/* <Tooltip placement="top" tooltipGap={10}>
            <TooltipTrigger>
              <Button
                disabled={disableGoogleSearchButton}
                className={styles.googleButton}
                size="small"
                variant="secondary"
                onClick={handleGoogleSearch}
              >
                <GoogleSearchIcon />
              </Button>
            </TooltipTrigger>
            {!disableGoogleSearchButton && (
              <TooltipContent className={cx('tooltipWrapper', styles.tooltip, 'max-w-[150px]')}>
                Click here to search {feedback.telephone} on Google
              </TooltipContent>
            )}
          </Tooltip> */}
        </div>
      </div>
      <CallBridge
        dispatchFeedback={dispatchFeedback}
        feedback={feedback}
        loanId={loanId}
        customerId={customerId}
        interactionType={InteractionType.CALL_BRIDGE}
        currentCallType={currentCallType}
        disableCallButton={isOptionDisabled}
      />
    </div>
  );
};

export default DefaultContacts;
